'use client';

import React, { useEffect, useRef, useState } from 'react';

function useInView(threshold = 0.1) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);

  return { ref, inView };
}

interface FormData {
  name: string;
  company: string;
  email: string;
  phone: string;
  service: string;
  message: string;
}

const serviceOptions = [
  'Business Consulting',
  'Technology Consulting',
  'Management Services',
  'Research & Analysis',
  'Engineering Services',
  'Accounting & Financial Advisory',
  'Other / Not Sure',
];

export default function ContactSection() {
  const { ref, inView } = useInView();
  const [formData, setFormData] = useState<FormData>({
    name: '',
    company: '',
    email: '',
    phone: '',
    service: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Partial<FormData>>({});

  const validate = (): boolean => {
    const newErrors: Partial<FormData> = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Invalid email';
    if (!formData.message.trim()) newErrors.message = 'Message is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      setSubmitted(true);
    }, 1500);
  };

  const handleChange = (field: keyof FormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: undefined }));
  };

  return (
    <section
      id="contact"
      className="py-24"
      style={{
        background: 'linear-gradient(180deg, #E8F0FE 0%, #F0F4FF 50%, #FFFFFF 100%)',
      }}
    >
      <div className="max-w-7xl mx-auto px-6">
        {/* Section header */}
        <div
          ref={ref}
          className={`text-center mb-16 transition-all duration-700 ${
            inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold mb-4"
            style={{
              background: 'rgba(66, 132, 245, 0.1)',
              border: '1px solid rgba(66, 132, 245, 0.2)',
              color: '#4284F5',
            }}
          >
            Get In Touch
          </div>
          <h2
            className="font-bold text-navy-dark mb-4"
            style={{ fontSize: 'clamp(28px, 4vw, 44px)' }}
          >
            Ready to Transform
            <span
              className="block"
              style={{
                background: 'linear-gradient(135deg, #4284F5, #21427B)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              Your Business?
            </span>
          </h2>
          <p className="text-gray-500 text-lg max-w-2xl mx-auto">
            Schedule a consultation with our expert team. We'll discuss your challenges and
            explore how Pluto7 Consulting can help you achieve your goals.
          </p>
        </div>

        <div
          className={`grid lg:grid-cols-5 gap-10 transition-all duration-700 delay-100 ${
            inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          {/* Left: Contact info */}
          <div className="lg:col-span-2 flex flex-col gap-6">
            {/* Company info card */}
            <div
              className="p-7 rounded-card"
              style={{
                background: 'linear-gradient(135deg, #0A1526 0%, #160F46 100%)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
              }}
            >
              <div className="flex items-center gap-3 mb-6">
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center"
                  style={{ background: 'rgba(66, 132, 245, 0.2)' }}
                >
                  <span className="text-white font-black text-sm">P7</span>
                </div>
                <div>
                  <div className="text-white font-bold">Pluto7 Consulting Inc.</div>
                  <div className="text-gray-400 text-xs">Business Consulting, NEC</div>
                </div>
              </div>

              <div className="flex flex-col gap-5">
                {[
                  {
                    icon: (
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    ),
                    label: 'Address',
                    value: '174 Hobbs Court, Milpitas, CA 95035, USA',
                  },
                  {
                    icon: (
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                    ),
                    label: 'Phone',
                    value: '(203) 627-7377',
                  },
                  {
                    icon: (
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    ),
                    label: 'Email',
                    value: 'support@plyto7.com',
                  },
                ].map((contact, i) => (
                  <div key={i} className="flex items-start gap-4">
                    <div
                      className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ background: 'rgba(66, 132, 245, 0.2)', color: '#60A5FA' }}
                    >
                      {contact.icon}
                    </div>
                    <div>
                      <div className="text-gray-400 text-xs mb-1">{contact.label}</div>
                      <div className="text-white text-sm font-medium">{contact.value}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Why consult card */}
            <div
              className="p-6 rounded-card"
              style={{
                background: 'white',
                border: '1px solid rgba(66, 132, 245, 0.1)',
                boxShadow: '0 4px 24px rgba(66, 132, 245, 0.08)',
              }}
            >
              <h4 className="font-bold text-navy-dark mb-4">What to Expect</h4>
              <div className="flex flex-col gap-3">
                {[
                  'Free 30-minute initial consultation',
                  'No-obligation assessment of your needs',
                  'Tailored proposal within 48 hours',
                  'Dedicated consultant assigned to your account',
                  'Flexible engagement models available',
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div
                      className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ background: 'rgba(0, 189, 165, 0.15)' }}
                    >
                      <svg className="w-3 h-3" style={{ color: '#00BDA5' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span className="text-gray-600 text-sm">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right: Contact form */}
          <div className="lg:col-span-3">
            <div
              className="p-8 rounded-card"
              style={{
                background: 'white',
                border: '1px solid rgba(66, 132, 245, 0.1)',
                boxShadow: '0 4px 24px rgba(66, 132, 245, 0.08)',
              }}
            >
              {submitted ? (
                <div className="text-center py-12">
                  <div
                    className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6"
                    style={{ background: 'rgba(0, 189, 165, 0.15)' }}
                  >
                    <svg className="w-10 h-10" style={{ color: '#00BDA5' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h3 className="text-2xl font-bold text-navy-dark mb-3">Thank You!</h3>
                  <p className="text-gray-500 max-w-sm mx-auto">
                    Your consultation request has been received. Our team will reach out within
                    24 business hours to schedule your free consultation.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} noValidate>
                  <h3 className="text-xl font-bold text-navy-dark mb-6">Request a Consultation</h3>

                  <div className="grid md:grid-cols-2 gap-5 mb-5">
                    {/* Name */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Full Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => handleChange('name', e.target.value)}
                        placeholder="John Smith"
                        className={`w-full px-4 py-3 rounded-lg text-sm outline-none transition-all duration-200 ${
                          errors.name ? 'border-red-400' : 'border-gray-200 focus:border-primary'
                        }`}
                        style={{
                          border: `1px solid ${errors.name ? '#F87171' : '#E5E7EB'}`,
                          background: '#F9FAFB',
                        }}
                      />
                      {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
                    </div>

                    {/* Company */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Company Name
                      </label>
                      <input
                        type="text"
                        value={formData.company}
                        onChange={(e) => handleChange('company', e.target.value)}
                        placeholder="Acme Corporation"
                        className="w-full px-4 py-3 rounded-lg text-sm outline-none transition-all duration-200 focus:border-primary"
                        style={{
                          border: '1px solid #E5E7EB',
                          background: '#F9FAFB',
                        }}
                      />
                    </div>

                    {/* Email */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email Address <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleChange('email', e.target.value)}
                        placeholder="john@company.com"
                        className={`w-full px-4 py-3 rounded-lg text-sm outline-none transition-all duration-200 ${
                          errors.email ? 'border-red-400' : 'focus:border-primary'
                        }`}
                        style={{
                          border: `1px solid ${errors.email ? '#F87171' : '#E5E7EB'}`,
                          background: '#F9FAFB',
                        }}
                      />
                      {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                    </div>

                    {/* Phone */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => handleChange('phone', e.target.value)}
                        placeholder="(555) 000-0000"
                        className="w-full px-4 py-3 rounded-lg text-sm outline-none transition-all duration-200 focus:border-primary"
                        style={{
                          border: '1px solid #E5E7EB',
                          background: '#F9FAFB',
                        }}
                      />
                    </div>
                  </div>

                  {/* Service Interest */}
                  <div className="mb-5">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Service Interest
                    </label>
                    <select
                      value={formData.service}
                      onChange={(e) => handleChange('service', e.target.value)}
                      className="w-full px-4 py-3 rounded-lg text-sm outline-none transition-all duration-200 focus:border-primary appearance-none"
                      style={{
                        border: '1px solid #E5E7EB',
                        background: '#F9FAFB',
                        color: formData.service ? '#111827' : '#9CA3AF',
                      }}
                    >
                      <option value="">Select a service...</option>
                      {serviceOptions.map((opt) => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  </div>

                  {/* Message */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Message <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={formData.message}
                      onChange={(e) => handleChange('message', e.target.value)}
                      placeholder="Tell us about your business challenges and what you're looking to achieve..."
                      rows={4}
                      className={`w-full px-4 py-3 rounded-lg text-sm outline-none transition-all duration-200 resize-none ${
                        errors.message ? 'border-red-400' : 'focus:border-primary'
                      }`}
                      style={{
                        border: `1px solid ${errors.message ? '#F87171' : '#E5E7EB'}`,
                        background: '#F9FAFB',
                      }}
                    />
                    {errors.message && <p className="text-red-500 text-xs mt-1">{errors.message}</p>}
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full py-4 text-white font-semibold rounded-btn transition-all duration-200 hover:opacity-90 hover:-translate-y-0.5 disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-3"
                    style={{
                      background: 'linear-gradient(291deg, #4284F5 0%, #21427B 100%)',
                      boxShadow: '0px 8px 24px rgba(66, 132, 245, 0.3)',
                    }}
                  >
                    {loading ? (
                      <>
                        <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Sending...
                      </>
                    ) : (
                      'Request a Consultation'
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
